import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { useProjectService } from '../../../contexts/ProjectServiceContext';
import { areaMeta } from './taskModel';

/**
 * Create a project in whichever space the surrounding provider is bound to.
 *
 * TITLE IS THE ONLY REQUIRED FIELD, deliberately. A project here is a label
 * that tasks point at; demanding a description and an area up front turns a
 * one-second action into a form, and the two optional fields can be filled in
 * later from the same place. The area picker is fed by the SPACE's offered
 * areas — passed in rather than read from canon, for the same reason the task
 * form takes them: this component must not know which space it is in.
 *
 * USES THE SAME OVERLAY/SHEET CLASSES AS TaskDetail (`.wt-overlay` + `.wt-sheet`),
 * NOT `.wt-sheet-overlay`. That one is the module's MOBILE filter sheet and
 * carries `display: none` above 768px — borrowing it once already shipped a
 * dialog that every test passed and no desktop user could see. Borrowing a
 * class means inheriting its contract.
 */
function ProjectForm({ offeredAreas = [], onClose, onCreated }) {
	const { currentUser } = useAuth();
	const projectService = useProjectService();
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [area, setArea] = useState('');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	const canSave = title.trim().length > 0 && !saving;

	const handleSave = async () => {
		if (!canSave) return;
		setSaving(true);
		setError('');
		try {
			const id = await projectService.createProject({
				title,
				description,
				area: area || null,
				createdBy: currentUser?.uid || null,
			});
			onCreated?.(id);
			onClose?.();
		} catch (e) {
			/* Surfaced, not swallowed. A create here can fail for a reason the
			   user can act on (a rules denial when signed in as the wrong
			   account), and the prior art in this repo is explicit that a silent
			   catch turns a working system into "it just did nothing". */
			console.error('Could not create the project:', e);
			setError("That didn't save. Check you're signed in as an owner.");
			setSaving(false);
		}
	};

	return (
		<div className="wt-overlay" onClick={onClose}>
			<div
				className="wt-sheet"
				role="dialog"
				aria-modal="true"
				aria-label="New project"
				onClick={(e) => e.stopPropagation()}>
				<div className="wt-sheet__head">
					<span className="wt-sheet__title">New project</span>
					<button
						type="button"
						className="wt-iconbtn"
						aria-label="Close"
						onClick={onClose}>
						<i className="fa-sharp fa-solid fa-xmark" />
					</button>
				</div>

				<div className="wt-sheet__body">
					<label className="wt-field">
						<span className="wt-field__label">Title</span>
						<input
							className="wt-input"
							value={title}
							autoFocus
							placeholder="What is this project?"
							onChange={(e) => setTitle(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') handleSave();
							}}
						/>
					</label>

					<label className="wt-field">
						<span className="wt-field__label">Description</span>
						<textarea
							className="wt-input"
							rows={3}
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</label>

					{offeredAreas.length > 0 && (
						<label className="wt-field">
							<span className="wt-field__label">Area</span>
							<select
								className="wt-input"
								value={area}
								onChange={(e) => setArea(e.target.value)}>
								<option value="">No area</option>
								{offeredAreas.map((a) => (
									<option key={a} value={a}>
										{areaMeta(a).label}
									</option>
								))}
							</select>
						</label>
					)}

					{error && <p className="wt-field__error">{error}</p>}
				</div>

				<div className="wt-sheet__foot">
					<button type="button" className="wt-btn" onClick={onClose}>
						Cancel
					</button>
					<button
						type="button"
						className="btn-cyv-teal"
						disabled={!canSave}
						onClick={handleSave}>
						{saving ? 'Creating…' : 'Create project'}
					</button>
				</div>
			</div>
		</div>
	);
}

export default ProjectForm;
